import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  X as XIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks';
import { useDocumentTitle } from '../../hooks/core';
import { authApi } from '../../api';
import { renderIcon } from '../../components';
import * as MuiIcons from '@mui/icons-material';

// Get all Material Icons (excluding internal/utility icons)
const iconNames = Object.keys(MuiIcons).filter(
  (name) =>
    name !== 'default' &&
    !name.endsWith('Outlined') &&
    !name.endsWith('Rounded') &&
    !name.endsWith('Sharp') &&
    !name.endsWith('TwoTone') &&
    !name.includes('Create')
);

// Popular icons to show first
const popularIcons = [
  'Star', 'Favorite', 'ThumbUp', 'EmojiEvents', 'SportsFootball',
  'SportsBasketball', 'SportsHockey', 'SportsSoccer', 'SportsBaseball',
  'Person', 'Face', 'Pets', 'Bolt', 'Whatshot', 'LocalFireDepartment',
  'Diamond', 'Casino', 'AttachMoney', 'Celebration', 'MilitaryTech',
  'Psychology', 'Rocket', 'AutoAwesome', 'Verified', 'Grade',
  'Crown', 'Shield', 'Gavel', 'WorkspacePremium', 'Stars',
];

export function ProfileSettingsPage() {
  const { user, refreshUser } = useAuth();

  useDocumentTitle('Profile Settings');
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [handle, setHandle] = useState(user?.handle || '');
  const [iconName, setIconName] = useState(user?.iconName || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Icon picker dialog state
  const [iconDialogOpen, setIconDialogOpen] = useState(false);
  const [iconSearch, setIconSearch] = useState('');

  // Email change state
  const [emailChangeDialogOpen, setEmailChangeDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangePending, setEmailChangePending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [pendingExpiresAt, setPendingExpiresAt] = useState<string | null>(null);
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState<string | null>(null);

  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Connected accounts state
  const [linkedAccounts, setLinkedAccounts] = useState<{ google: boolean; facebook: boolean; twitter: boolean; hasPassword: boolean } | null>(null);
  const [linkedAccountsLoading, setLinkedAccountsLoading] = useState(true);
  const [linkedAccountsError, setLinkedAccountsError] = useState<string | null>(null);
  const [linkedAccountsSuccess, setLinkedAccountsSuccess] = useState<string | null>(null);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [unlinkProvider, setUnlinkProvider] = useState<'google' | 'facebook' | 'twitter' | null>(null);
  const [unlinkLoading, setUnlinkLoading] = useState(false);

  // Check for pending email change on mount
  useEffect(() => {
    const checkPendingEmailChange = async () => {
      try {
        const result = await authApi.getPendingEmailChange();
        if (result.pending && result.newEmail) {
          setEmailChangePending(true);
          setPendingEmail(result.newEmail);
          setPendingExpiresAt(result.expiresAt || null);
        }
      } catch {
        // Ignore errors - just means no pending change
      }
    };
    checkPendingEmailChange();
  }, []);

  // Fetch linked accounts on mount
  useEffect(() => {
    const fetchLinkedAccounts = async () => {
      try {
        const accounts = await authApi.getLinkedAccounts();
        setLinkedAccounts(accounts);
      } catch {
        // Ignore errors - will show "unable to load" state
      } finally {
        setLinkedAccountsLoading(false);
      }
    };
    fetchLinkedAccounts();
  }, []);

  const handleRequestEmailChange = async () => {
    if (!newEmail || newEmail === user?.email) {
      setEmailChangeError('Please enter a different email address');
      return;
    }

    setEmailChangeLoading(true);
    setEmailChangeError(null);

    try {
      await authApi.requestEmailChange(newEmail);
      setEmailChangePending(true);
      setPendingEmail(newEmail);
      setEmailChangeDialogOpen(false);
      setEmailChangeSuccess('Verification email sent to ' + newEmail);
      setNewEmail('');
    } catch (err) {
      setEmailChangeError(err instanceof Error ? err.message : 'Failed to request email change');
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleCancelEmailChange = async () => {
    setEmailChangeLoading(true);
    setEmailChangeError(null);

    try {
      await authApi.cancelEmailChange();
      setEmailChangePending(false);
      setPendingEmail(null);
      setPendingExpiresAt(null);
      setEmailChangeSuccess('Email change request cancelled');
    } catch (err) {
      setEmailChangeError(err instanceof Error ? err.message : 'Failed to cancel email change');
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setEmailChangeLoading(true);
    setEmailChangeError(null);

    try {
      await authApi.resendEmailChangeVerification();
      setEmailChangeSuccess('Verification email resent to ' + pendingEmail);
    } catch (err) {
      setEmailChangeError(err instanceof Error ? err.message : 'Failed to resend verification');
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);

    // Validate passwords
    if (!currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }
    if (!newPassword) {
      setPasswordError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setPasswordLoading(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully');
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordError(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleLinkGoogle = async () => {
    setLinkedAccountsError(null);
    setLinkedAccountsSuccess(null);

    try {
      // Get the SSO auth URL for Google with a special state indicating this is a link operation
      const state = crypto.randomUUID();
      sessionStorage.setItem('googleLinkState', state);
      const { authUrl } = await authApi.getSSOAuthURL('google', state);
      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (err) {
      setLinkedAccountsError(err instanceof Error ? err.message : 'Failed to start Google linking');
    }
  };

  const handleUnlinkGoogle = async () => {
    setUnlinkLoading(true);
    setLinkedAccountsError(null);

    try {
      await authApi.unlinkGoogleAccount();
      setLinkedAccounts((prev) => prev ? { ...prev, google: false } : null);
      setLinkedAccountsSuccess('Google account unlinked successfully');
      setUnlinkDialogOpen(false);
      await refreshUser();
    } catch (err) {
      setLinkedAccountsError(err instanceof Error ? err.message : 'Failed to unlink Google account');
    } finally {
      setUnlinkLoading(false);
    }
  };

  const handleLinkFacebook = async () => {
    setLinkedAccountsError(null);
    setLinkedAccountsSuccess(null);

    try {
      const state = crypto.randomUUID();
      sessionStorage.setItem('facebookLinkState', state);
      const { authUrl } = await authApi.getSSOAuthURL('facebook', state);
      window.location.href = authUrl;
    } catch (err) {
      setLinkedAccountsError(err instanceof Error ? err.message : 'Failed to start Facebook linking');
    }
  };

  const handleUnlinkFacebook = async () => {
    setUnlinkLoading(true);
    setLinkedAccountsError(null);

    try {
      await authApi.unlinkFacebookAccount();
      setLinkedAccounts((prev) => prev ? { ...prev, facebook: false } : null);
      setLinkedAccountsSuccess('Facebook account unlinked successfully');
      setUnlinkDialogOpen(false);
      await refreshUser();
    } catch (err) {
      setLinkedAccountsError(err instanceof Error ? err.message : 'Failed to unlink Facebook account');
    } finally {
      setUnlinkLoading(false);
    }
  };

  const handleLinkTwitter = async () => {
    setLinkedAccountsError(null);
    setLinkedAccountsSuccess(null);

    try {
      const state = crypto.randomUUID();
      sessionStorage.setItem('twitterLinkState', state);
      const { authUrl } = await authApi.getSSOAuthURL('twitter', state);
      window.location.href = authUrl;
    } catch (err) {
      setLinkedAccountsError(err instanceof Error ? err.message : 'Failed to start Twitter/X linking');
    }
  };

  const handleUnlinkTwitter = async () => {
    setUnlinkLoading(true);
    setLinkedAccountsError(null);

    try {
      await authApi.unlinkTwitterAccount();
      setLinkedAccounts((prev) => prev ? { ...prev, twitter: false } : null);
      setLinkedAccountsSuccess('Twitter/X account unlinked successfully');
      setUnlinkDialogOpen(false);
      await refreshUser();
    } catch (err) {
      setLinkedAccountsError(err instanceof Error ? err.message : 'Failed to unlink Twitter/X account');
    } finally {
      setUnlinkLoading(false);
    }
  };

  // Handle OAuth callback for account linking (check URL params on mount)
  useEffect(() => {
    let isMounted = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const savedGoogleState = sessionStorage.getItem('googleLinkState');
    const savedFacebookState = sessionStorage.getItem('facebookLinkState');
    const savedTwitterState = sessionStorage.getItem('twitterLinkState');

    // Determine which provider this callback is for
    let provider: 'google' | 'facebook' | 'twitter' | null = null;
    if (code && state) {
      if (savedGoogleState && state === savedGoogleState) {
        sessionStorage.removeItem('googleLinkState');
        provider = 'google';
      } else if (savedFacebookState && state === savedFacebookState) {
        sessionStorage.removeItem('facebookLinkState');
        provider = 'facebook';
      } else if (savedTwitterState && state === savedTwitterState) {
        sessionStorage.removeItem('twitterLinkState');
        provider = 'twitter';
      }
    }

    if (provider && code) {
      // Remove query params from URL
      window.history.replaceState({}, '', window.location.pathname);

      const linkAccount = async () => {
        setLinkedAccountsLoading(true);
        try {
          if (provider === 'google') {
            await authApi.linkGoogleAccount(code);
            if (isMounted) {
              setLinkedAccounts((prev) => prev ? { ...prev, google: true } : { google: true, facebook: false, twitter: false, hasPassword: true });
              setLinkedAccountsSuccess('Google account linked successfully');
            }
          } else if (provider === 'facebook') {
            await authApi.linkFacebookAccount(code);
            if (isMounted) {
              setLinkedAccounts((prev) => prev ? { ...prev, facebook: true } : { google: false, facebook: true, twitter: false, hasPassword: true });
              setLinkedAccountsSuccess('Facebook account linked successfully');
            }
          } else if (provider === 'twitter') {
            await authApi.linkTwitterAccount(code, state!);
            if (isMounted) {
              setLinkedAccounts((prev) => prev ? { ...prev, twitter: true } : { google: false, facebook: false, twitter: true, hasPassword: true });
              setLinkedAccountsSuccess('Twitter/X account linked successfully');
            }
          }
          if (isMounted) {
            await refreshUser();
          }
        } catch (err) {
          if (isMounted) {
            setLinkedAccountsError(err instanceof Error ? err.message : `Failed to link ${provider} account`);
          }
        } finally {
          if (isMounted) {
            setLinkedAccountsLoading(false);
          }
        }
      };
      linkAccount();
    }

    return () => {
      isMounted = false;
    };
  }, [refreshUser]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await authApi.updateProfile({
        displayName: displayName !== user?.displayName ? displayName : undefined,
        handle: handle !== user?.handle ? handle : undefined,
        iconName: iconName !== user?.iconName ? iconName : undefined,
      });
      await refreshUser();
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getDisplayContent = () => {
    if (iconName && renderIcon(iconName)) {
      return renderIcon(iconName, 40);
    }
    if (handle) {
      return handle.toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  };

  // Filter icons based on search
  const filteredIcons = (() => {
    const searchLower = iconSearch.toLowerCase();
    if (!searchLower) {
      const others = iconNames.filter((name) => !popularIcons.includes(name));
      return [...popularIcons.filter((name) => iconNames.includes(name)), ...others];
    }
    return iconNames.filter((name) => name.toLowerCase().includes(searchLower));
  })();

  const handleOpenIconDialog = () => {
    setIconSearch('');
    setIconDialogOpen(true);
  };

  const handleSelectIcon = (name: string) => {
    setIconName(name);
    setIconDialogOpen(false);
  };

  const handleClearIcon = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIconName('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SettingsIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4">Profile Settings</Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your display name, handle, and icon that appear on your squares.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
          Profile updated successfully!
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          {/* Avatar with icon picker overlay */}
          <Box sx={{ position: 'relative', mr: 3 }}>
            <Avatar
              variant="rounded"
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: iconName ? undefined : '1.5rem',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.9,
                },
              }}
              onClick={handleOpenIconDialog}
            >
              {getDisplayContent()}
            </Avatar>
            {/* Edit button overlay */}
            <Tooltip title={iconName ? 'Change icon' : 'Choose icon'}>
              <IconButton
                size="small"
                onClick={handleOpenIconDialog}
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Clear button when icon is set */}
            {iconName && (
              <Tooltip title="Remove icon">
                <IconButton
                  size="small"
                  onClick={handleClearIcon}
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'white',
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box>
            <Typography variant="h6">{displayName || user?.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
            {handle && (
              <Typography variant="body2" color="primary">
                @{handle.toUpperCase()}
              </Typography>
            )}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your squares will display your chosen icon if set. If no icon is selected,
          your 4-character handle will be shown. Hover over any square to see the
          player's handle.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Display Name"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              inputProps={{ 'data-testid': 'profile-display-name-input' }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Handle"
              fullWidth
              value={handle}
              onChange={(e) => setHandle(e.target.value.toUpperCase().slice(0, 4))}
              inputProps={{ maxLength: 4, 'data-testid': 'profile-handle-input' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">@</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            data-testid="profile-save-button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>

      {/* Email and Password Settings Row */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        {/* Email Settings Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <EmailIcon color="primary" />
              <Typography variant="h6">Email Address</Typography>
            </Box>

            {emailChangeError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEmailChangeError(null)}>
                {emailChangeError}
              </Alert>
            )}

            {emailChangeSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEmailChangeSuccess(null)}>
                {emailChangeSuccess}
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">
                All notifications will be sent to this address: <strong>{user?.email}</strong>.
              </Typography>
            </Box>

            {emailChangePending ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Email change pending
                  </Typography>
                  <Typography variant="body2">
                    A verification email was sent to <strong>{pendingEmail}</strong>.
                    Please check your inbox and click the verification link to complete the change.
                  </Typography>
                  {pendingExpiresAt && (
                    <Typography variant="caption" color="text.secondary">
                      Expires: {new Date(pendingExpiresAt).toLocaleString()}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleResendVerification}
                      disabled={emailChangeLoading}
                    >
                      {emailChangeLoading ? <CircularProgress size={16} /> : 'Resend Email'}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={handleCancelEmailChange}
                      disabled={emailChangeLoading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </Alert>
            ) : (
              <Button
                variant="outlined"
                onClick={() => setEmailChangeDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Change Email
              </Button>
            )}
          </Paper>
        </Grid>

        {/* Password Settings Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <LockIcon color="primary" />
              <Typography variant="h6">Password</Typography>
            </Box>

            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess(null)}>
                {passwordSuccess}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Change your password to keep your account secure. You'll need to enter your current password to confirm.
            </Typography>

            <Button
              variant="outlined"
              onClick={() => setPasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Connected Accounts Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <LinkIcon color="primary" />
          <Typography variant="h6">Connected Accounts</Typography>
        </Box>

        {linkedAccountsError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLinkedAccountsError(null)}>
            {linkedAccountsError}
          </Alert>
        )}

        {linkedAccountsSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLinkedAccountsSuccess(null)}>
            {linkedAccountsSuccess}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Connect your account to sign in with social providers. You can use your password or any connected account to sign in.
        </Typography>

        {linkedAccountsLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading...</Typography>
          </Box>
        ) : linkedAccounts ? (
          <>
            {/* Google */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  component="img"
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  sx={{ width: 24, height: 24 }}
                />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Google</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {linkedAccounts.google ? 'Connected' : 'Not connected'}
                  </Typography>
                </Box>
              </Box>
              {linkedAccounts.google ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<LinkOffIcon />}
                  onClick={() => { setUnlinkProvider('google'); setUnlinkDialogOpen(true); }}
                  disabled={!linkedAccounts.hasPassword && !linkedAccounts.facebook && !linkedAccounts.twitter}
                >
                  Unlink
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={handleLinkGoogle}
                >
                  Connect
                </Button>
              )}
            </Box>

            {/* Facebook */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  component="img"
                  src="https://www.facebook.com/images/fb_icon_325x325.png"
                  alt="Facebook"
                  sx={{ width: 24, height: 24 }}
                />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Facebook</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {linkedAccounts.facebook ? 'Connected' : 'Not connected'}
                  </Typography>
                </Box>
              </Box>
              {linkedAccounts.facebook ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<LinkOffIcon />}
                  onClick={() => { setUnlinkProvider('facebook'); setUnlinkDialogOpen(true); }}
                  disabled={!linkedAccounts.hasPassword && !linkedAccounts.google && !linkedAccounts.twitter}
                >
                  Unlink
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={handleLinkFacebook}
                >
                  Connect
                </Button>
              )}
            </Box>

            {/* Twitter/X */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <XIcon sx={{ width: 24, height: 24 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Twitter / X</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {linkedAccounts.twitter ? 'Connected' : 'Not connected'}
                  </Typography>
                </Box>
              </Box>
              {linkedAccounts.twitter ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<LinkOffIcon />}
                  onClick={() => { setUnlinkProvider('twitter'); setUnlinkDialogOpen(true); }}
                  disabled={!linkedAccounts.hasPassword && !linkedAccounts.google && !linkedAccounts.facebook}
                >
                  Unlink
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={handleLinkTwitter}
                >
                  Connect
                </Button>
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Unable to load connected accounts.
          </Typography>
        )}

        {linkedAccounts && !linkedAccounts.hasPassword && (
          // Show info alert when user has only one SSO provider and no password
          // They need to add a password or another provider before they can unlink
          [linkedAccounts.google, linkedAccounts.facebook, linkedAccounts.twitter].filter(Boolean).length === 1
        ) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You signed up with a social account and don't have a password set. To unlink your connected account, first{' '}
            <a href="/forgot-password" style={{ color: 'inherit' }}>set a password</a> or connect another sign-in method above.
          </Alert>
        )}
      </Paper>

      {/* Unlink Account Confirmation Dialog */}
      <Dialog
        open={unlinkDialogOpen}
        onClose={() => { setUnlinkDialogOpen(false); setUnlinkProvider(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unlink {unlinkProvider === 'google' ? 'Google' : unlinkProvider === 'facebook' ? 'Facebook' : 'Twitter/X'} Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to unlink your {unlinkProvider === 'google' ? 'Google' : unlinkProvider === 'facebook' ? 'Facebook' : 'Twitter/X'} account? You will no longer be able to sign in with {unlinkProvider === 'google' ? 'Google' : unlinkProvider === 'facebook' ? 'Facebook' : 'Twitter/X'},
            but you can still sign in with your email and password or other connected accounts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUnlinkDialogOpen(false); setUnlinkProvider(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={unlinkProvider === 'google' ? handleUnlinkGoogle : unlinkProvider === 'facebook' ? handleUnlinkFacebook : handleUnlinkTwitter}
            disabled={unlinkLoading}
          >
            {unlinkLoading ? <CircularProgress size={20} /> : `Unlink ${unlinkProvider === 'google' ? 'Google' : unlinkProvider === 'facebook' ? 'Facebook' : 'Twitter/X'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your current password and choose a new password.
            If you don't remember your current password, you can use the{' '}
            <a href="/forgot-password" style={{ color: 'inherit' }}>forgot password</a> option instead.
          </Typography>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
            inputProps={{ 'data-testid': 'profile-current-password-input' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Must be at least 8 characters"
            inputProps={{ 'data-testid': 'profile-new-password-input' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword !== '' && confirmPassword !== newPassword}
            helperText={confirmPassword !== '' && confirmPassword !== newPassword ? 'Passwords do not match' : ''}
            inputProps={{ 'data-testid': 'profile-confirm-password-input' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} data-testid="profile-password-cancel-button">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
            data-testid="profile-password-submit-button"
          >
            {passwordLoading ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Change Dialog */}
      <Dialog
        open={emailChangeDialogOpen}
        onClose={() => {
          setEmailChangeDialogOpen(false);
          setEmailChangeError(null);
          setNewEmail('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Email Address</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your new email address. We'll send a verification link to confirm the change.
            Your email won't be changed until you click the link in the verification email.
          </Typography>
          {emailChangeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {emailChangeError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="New Email Address"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Enter new email"
            autoFocus
            inputProps={{ 'data-testid': 'profile-new-email-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEmailChangeDialogOpen(false);
              setEmailChangeError(null);
              setNewEmail('');
            }}
            data-testid="profile-email-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRequestEmailChange}
            disabled={emailChangeLoading || !newEmail}
            data-testid="profile-email-submit-button"
          >
            {emailChangeLoading ? <CircularProgress size={20} /> : 'Send Verification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Icon Picker Dialog */}
      <Dialog
        open={iconDialogOpen}
        onClose={() => setIconDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Choose an Icon</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search icons..."
            value={iconSearch}
            onChange={(e) => setIconSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
            autoFocus
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
              gap: 1,
              maxHeight: 400,
              overflowY: 'auto',
            }}
          >
            {filteredIcons.slice(0, 200).map((name) => (
              <Tooltip key={name} title={name}>
                <IconButton
                  onClick={() => handleSelectIcon(name)}
                  sx={{
                    border: iconName === name ? 2 : 1,
                    borderColor: iconName === name ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 1,
                    position: 'relative',
                  }}
                >
                  {renderIcon(name, 28)}
                  {iconName === name && (
                    <CheckIcon
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        fontSize: 12,
                        color: 'primary.main',
                      }}
                    />
                  )}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          {filteredIcons.length > 200 && (
            <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
              Showing first 200 results. Use search to find more icons.
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIconDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
